import React, { useEffect, useState } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Box, Button, FormControl, Grid } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import AuditChecklistHistoryApiService from '../../api/AuditChecklistHistoryApiService';
import AuditChecklistHistoryItemApiService from '../../api/AuditChecklistHistoryItemApiService';
import TaskApiService from '../../api/TaskApiService';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SaveButton from '../Common/SaveButton';
import { useAsyncCallback } from '../../hooks/useAsyncCallback';
import { useSnackbar } from '../Common/SnackbarProvider';

const auditChecklistHistoryApiService = new AuditChecklistHistoryApiService();
const auditChecklistHistoryItemApiService = new AuditChecklistHistoryItemApiService();
const taskApiService = new TaskApiService();

export default function StartAuditChecklistModal (props) {
    const {
        open,
        currentAuditChecklist,
        onCancel,
        onSave,
    } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const [auditChecklistItems, setAuditChecklistItems] = useState([]);
    const [openAuditChecklistTasksModal, setOpenAuditChecklistTasksModal] = useState(false);
    const [tasksCreated, setTasksCreated] = useState([]);

    const tasksCreatedColumns = [
        {
            field: 'taskId',
            headerName: t('startAudit.column.task'),
            flex: 1,
            renderCell: (params) => {
                return (
                    <Link
                        to={`/Task/${params.value}`}
                        style={{
                            textDecoration: 'underline',
                            color: 'blue',
                            cursor: 'pointer',
                        }}
                    >
                        {params.value}
                    </Link>
                );
            },
        },
        {
            field: 'prompt',
            headerName: t('startAudit.column.checklistPrompt'),
            flex: 3,
        },
    ];

    useEffect(() => {
        if (open) {
            const items = (currentAuditChecklist.auditChecklistItems || []).map(item => ({
                ...item,
                response: '',
                childItems: (item.childItems || []).map(child => ({
                    ...child,
                    response: '',
                })),
            }));

            setAuditChecklistItems(items);
            setTasksCreated([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    function handleCancel () {
        onCancel();
    }

    const { run: handleSave, loading: saving } = useAsyncCallback(async () => {
        // Validate all visible prompts are answered
        for (const checklist of auditChecklistItems) {
            if (checklist.response === '') {
                snackbar.error(t('startAudit.validation.allPromptsRequired'));
                return;
            }

            // Validate visible sub-questions
            for (const child of (checklist.childItems || [])) {
                if (child.ciTriggerAnswer === checklist.response && child.response === '') {
                    snackbar.error(t('startAudit.validation.allSubQuestionsRequired'));
                    return;
                }
            }
        }

        let auditChecklistHistoryResponse = await auditChecklistHistoryApiService.addAuditChecklistHistory(getAuditChecklistHistory());
        let auditChecklistHistoryId = auditChecklistHistoryResponse.data.chId;

        await getAuditChecklistHistoryItems(auditChecklistHistoryId);

        snackbar.success(t('startAudit.toast.complete', { title: currentAuditChecklist.acTitle }));
        onSave();
        setOpenAuditChecklistTasksModal(true);
    });

    function handleOk () {
        setOpenAuditChecklistTasksModal(false);
    }

    function shouldCreateTask(item) {
        return item.ciCreateTask !== false && item.response === '1';
    }

    async function createTaskForItem(item, parentItem) {
        let checklistTask = {
            shortDescription: item.ciPrompt,
            projectId: currentAuditChecklist.projectId,
            categoryId: item.ciCategoryId || parentItem?.ciCategoryId || null,
            priorityId: item.ciPriorityId || parentItem?.ciPriorityId || null,
            statusId: item.ciStatusId || parentItem?.ciStatusId || null,
        };

        let basicTaskResponse = await taskApiService.addNewBasicTask(checklistTask);

        let checklistTaskComment = {
            taskChange: t('startAudit.taskComment', { prompt: item.ciPrompt, checklist: currentAuditChecklist.acTitle }),
            taskId: basicTaskResponse.data,
            taskChangeType: 'Comment',
        };

        await taskApiService.addTaskComment(checklistTaskComment);

        return basicTaskResponse.data;
    }

    async function getAuditChecklistHistoryItems (auditChecklistHistoryId) {
        let tempAuditChecklistItems = [];
        let tasksCreated = [];

        for (const checklist of auditChecklistItems) {
            let taskId = null;

            if (shouldCreateTask(checklist)) {
                taskId = await createTaskForItem(checklist, null);
                tasksCreated.push({ taskId, prompt: checklist.ciPrompt });
            }

            tempAuditChecklistItems.push({
                hiAuditChecklistHistoryId: auditChecklistHistoryId,
                hiPrompt: checklist.ciPrompt,
                hiSortSeq: checklist.ciSortSeq,
                taskId: taskId,
                hiAnswer: checklist.response,
            });

            // Process visible sub-questions
            for (const child of (checklist.childItems || [])) {
                if (child.ciTriggerAnswer !== checklist.response) continue;

                let childTaskId = null;

                if (shouldCreateTask(child)) {
                    childTaskId = await createTaskForItem(child, checklist);
                    tasksCreated.push({ taskId: childTaskId, prompt: child.ciPrompt });
                }

                tempAuditChecklistItems.push({
                    hiAuditChecklistHistoryId: auditChecklistHistoryId,
                    hiPrompt: child.ciPrompt,
                    hiSortSeq: child.ciSortSeq,
                    taskId: childTaskId,
                    hiAnswer: child.response,
                });
            }
        }

        await auditChecklistHistoryItemApiService.addAuditChecklistHistoryItems(tempAuditChecklistItems);

        if (tasksCreated.length > 0) {
            setTasksCreated(tasksCreated);
        }
    }

    function getAuditChecklistHistory () {
        return {
            chTitle: currentAuditChecklist.acTitle,
            chAuditChecklistsId: currentAuditChecklist.acId,
        }
    }

    const handleRadioChange = (index, value) => {
        setAuditChecklistItems(prev => prev.map((item, i) => {
            if (i !== index) return item;
            return { ...item, response: value };
        }));
    };

    const handleSubQuestionRadioChange = (parentIndex, childIndex, value) => {
        setAuditChecklistItems(prev => prev.map((item, i) => {
            if (i !== parentIndex) return item;
            const updatedChildren = (item.childItems || []).map((child, ci) => {
                if (ci !== childIndex) return child;
                return { ...child, response: value };
            });
            return { ...item, childItems: updatedChildren };
        }));
    };

    return (
        <>
            <Dialog
                sx={{ '& .MuiDialog-paper': { width: '100%' } }}
                maxWidth="xl"
                fullWidth
                open={open}>
                <DialogTitle>{t('startAudit.title', { title: currentAuditChecklist.acTitle })}</DialogTitle>
                <DialogContent dividers>
                    <Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                {auditChecklistItems.map((checklist, index) => (
                                    <div key={index}>
                                        <Grid item xs={12} justifyContent="space-between" alignItems="center">
                                            <Grid item>
                                                {checklist.ciPrompt}
                                            </Grid>
                                            <Grid item>
                                                <FormControl>
                                                    <RadioGroup
                                                        row
                                                        onChange={(event) => {
                                                            handleRadioChange(index, event.target.value);
                                                        }}
                                                        value={checklist.response}
                                                    >
                                                        <FormControlLabel value={'0'} control={<Radio />} label={t('common.yes')} />
                                                        <FormControlLabel value={'1'} control={<Radio />} label={t('common.no')} />
                                                    </RadioGroup>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                        {/* Conditional sub-questions */}
                                        {checklist.response !== '' && (checklist.childItems || []).map((child, childIndex) => {
                                            if (child.ciTriggerAnswer !== checklist.response) return null;
                                            return (
                                                <Grid
                                                    key={`sub-${index}-${childIndex}`}
                                                    item
                                                    xs={12}
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    sx={{ ml: 4, borderLeft: '3px solid #1976d2', pl: 2, mb: 1 }}
                                                >
                                                    <Grid item>
                                                        {child.ciPrompt}
                                                    </Grid>
                                                    <Grid item>
                                                        <FormControl>
                                                            <RadioGroup
                                                                row
                                                                onChange={(event) => {
                                                                    handleSubQuestionRadioChange(index, childIndex, event.target.value);
                                                                }}
                                                                value={child.response}
                                                            >
                                                                <FormControlLabel value={'0'} control={<Radio />} label={t('common.yes')} />
                                                                <FormControlLabel value={'1'} control={<Radio />} label={t('common.no')} />
                                                            </RadioGroup>
                                                        </FormControl>
                                                    </Grid>
                                                </Grid>
                                            );
                                        })}
                                    </div>
                                ))}
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button autoFocus onClick={handleCancel} disabled={saving}>
                        {t('common.cancel')}
                    </Button>
                    <SaveButton loading={saving} variant="contained" onClick={handleSave}>{t('startAudit.button.complete')}</SaveButton>
                </DialogActions>
            </Dialog>
            <Dialog
                sx={{ '& .MuiDialog-paper': { width: '100%' } }}
                maxWidth="lg"
                fullWidth
                open={openAuditChecklistTasksModal}>
                <DialogTitle>{t('startAudit.tasksTitle', { title: currentAuditChecklist.acTitle })}</DialogTitle>
                <DialogContent dividers>
                    <Box>
                        <DataGridPro
                            rows={tasksCreated}
                            columns={tasksCreatedColumns}
                            getRowId={(row) => row.taskId}
                            localeText={{ noRowsLabel: t('startAudit.help.noTasksCreated') }}
                            autoHeight
                            disableColumnMenu
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" autoFocus onClick={handleOk}>
                        {t('common.ok')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>

    );
}
